/**
 * Bar-Tavern HTTP Client
 * Routes requests to the bar-tavern backend (separate from multi-rater and property)
 *
 * Error messages go through `readBarTavernErrorMessage`. Two envelopes are in play on this
 * backend (`{"msg"}` from our own handlers, `{"detail"}` from FastAPI); only one used to be read.
 */

import { parseResponse } from './response-parser';
import { FetchError } from './errors';
import { DEFAULT_TIMEOUT, HTTP_METHODS } from './constants';
import type { FetchOptions } from './types';

const BAR_TAVERN_BASE_URL =
  process.env.NEXT_PUBLIC_BAR_TAVERN_API_URL ||
  process.env.BAR_TAVERN_API_URL ||
  'http://localhost:8000';

/**
 * The message to carry on a `FetchError`, read from the response body when it has one.
 *
 * Reads `msg` too, not just `detail`: this backend's own failures are `{"msg": "..."}`
 * (see `_error()` in `api/api/v1/applications/locations_routes.py`), while only FastAPI's
 * `HTTPException` uses `{"detail": ...}`. `detail` wins when both are present. `detail` can
 * also be an ARRAY (FastAPI's 422 shape) — interpolating that directly produces
 * "[object Object]", so take the first issue's `msg` instead.
 *
 * The reason-phrase fallback must stay a distinct sentence, not a generic one: a 503 from
 * ingress-nginx carries no JSON at all, and the phrase is the only thing on screen telling
 * "the API rejected this" apart from "nothing was listening".
 */
async function readBarTavernErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const detail = body?.detail;
    if (typeof detail === 'string' && detail.trim() !== '') return detail;
    if (Array.isArray(detail)) {
      const first = detail[0]?.msg;
      if (typeof first === 'string' && first.trim() !== '') return first;
    }
    const msg = body?.msg;
    if (typeof msg === 'string' && msg.trim() !== '') return msg;
  } catch {
    // Not JSON: an ingress error page, a plain-text 500, or an empty body.
  }

  // 502/503/504 with nothing usable in the body means the EDGE failed, not the API — name
  // that layer explicitly. No "try again" wording: this client also serves POSTs that
  // create rows, and a lost response doesn't mean a lost write.
  if (response.status >= 502 && response.status <= 504) {
    return `The bar-tavern API is temporarily unavailable (${response.status} ${response.statusText}).`;
  }

  return `HTTP error: ${response.statusText}`;
}

async function barTavernFetch<T>(
  path: string,
  options?: FetchOptions,
): Promise<T> {
  const { params, timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options || {};

  const cleanPath = path.replace(/^\//, '');
  const fullUrl = new URL(`${BAR_TAVERN_BASE_URL}/${cleanPath}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        fullUrl.searchParams.set(key, String(value));
      }
    });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(fullUrl.toString(), {
      ...fetchOptions,
      signal: controller.signal,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
    });

    if (!response.ok) {
      const errorMessage = await readBarTavernErrorMessage(response);
      throw new FetchError(
        errorMessage,
        response.status,
        response.statusText,
        fullUrl.toString(),
        fetchOptions.method,
      );
    }

    return parseResponse<T>(response);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new FetchError(
        'Request timeout',
        408,
        'Request Timeout',
        fullUrl.toString(),
        fetchOptions.method,
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const barTavernGet = <T>(
  url: string,
  options?: FetchOptions,
): Promise<T> => barTavernFetch<T>(url, { ...options, method: HTTP_METHODS.GET, timeout: 150000 });

export const barTavernPatch = <T>(
  url: string,
  body: unknown,
  options?: FetchOptions,
): Promise<T> =>
  barTavernFetch<T>(url, {
    ...options,
    method: HTTP_METHODS.PATCH,
    body: JSON.stringify(body),
  });

export const barTavernPost = <T>(
  url: string,
  body: unknown,
  options?: FetchOptions,
): Promise<T> =>
  barTavernFetch<T>(url, {
    ...options,
    method: HTTP_METHODS.POST,
    body: JSON.stringify(body),
  });

/**
 * PUT, added for the admin coverage-defaults endpoint (BAR-240), which replaces all
 * three default charges at once rather than patching one. The verb matters: a PATCH
 * to that endpoint would suggest per-field updates the backend does not offer.
 */
export const barTavernPut = <T>(
  url: string,
  body: unknown,
  options?: FetchOptions,
): Promise<T> =>
  barTavernFetch<T>(url, {
    ...options,
    method: HTTP_METHODS.PUT,
    body: JSON.stringify(body),
  });

export const barTavernDelete = <T>(
  url: string,
  options?: FetchOptions,
): Promise<T> =>
  barTavernFetch<T>(url, {
    ...options,
    method: HTTP_METHODS.DELETE,
  });

export async function barTavernPostFormData<T>(
  path: string,
  formData: FormData,
  options?: Omit<FetchOptions, 'body'>,
): Promise<T> {
  const { params, timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options || {};

  const cleanPath = path.replace(/^\//, '');
  const fullUrl = new URL(`${BAR_TAVERN_BASE_URL}/${cleanPath}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        fullUrl.searchParams.set(key, String(value));
      }
    });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const { 'Content-Type': _, ...headers } = (fetchOptions.headers || {}) as Record<string, string>;

    const response = await fetch(fullUrl.toString(), {
      ...fetchOptions,
      method: HTTP_METHODS.POST,
      body: formData,
      signal: controller.signal,
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      const errorMessage = await readBarTavernErrorMessage(response);
      throw new FetchError(
        errorMessage,
        response.status,
        response.statusText,
        fullUrl.toString(),
        'POST',
      );
    }

    return parseResponse<T>(response);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new FetchError(
        'Request timeout',
        408,
        'Request Timeout',
        fullUrl.toString(),
        'POST',
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}
